import DistributionService from "@/services/distribution/";
import { DISTRIBUTION_CHANNEL, QUESTION_TYPE } from "@/consts/survey";
import { TEMPLATE_TYPES } from "@/consts/template";
import ErrorHandler from "@/tools/errorHandler";
import { i18n } from "@/boot/i18n";
import { METADATA_KIND } from "@/consts/metadata";
import * as types from "./mutationTypes";
import getEmailLayout from "../../../utils/getEmailLayout";
import {
  validateCustomerFile,
  validateMessages,
  validateTabChannel,
  validateTabCustomer,
  validateTabSchedule,
  validateValidityCreatedAt
} from "./validators";
import MapCustomerData from "./mapping";

export default {
  clearNewState({ dispatch, commit }) {
    dispatch("template/clearTemplates", null, { root: true });
    dispatch("integration/clearIntegrations", null, { root: true });
    dispatch("integration/clearWhatsappTemplates", null, { root: true });
    commit(types.CLEAR_WHATSAPP_INTEGRATIONS);
    dispatch("survey/clearSurvey", null, { root: true });
    dispatch("metadata/clearMetadatas", null, { root: true });
    dispatch("metadata/clearMetadata", null, { root: true });
    dispatch("validator/clearValidator", null, { root: true });
    dispatch("clearDistribution");
  },
  clearStateForTemplatePage({ dispatch }) {
    dispatch("template/clearTemplates", null, { root: true });
    dispatch("metadata/clearMetadatas", null, { root: true });
    dispatch("metadata/clearMetadata", null, { root: true });
    dispatch("validator/clearValidator", null, { root: true });
  },
  clearDistributions({ commit }) {
    commit(types.CLEAR_DISTRIBUTIONS);
    commit(types.CLEAR_QUERY_PARAMS);
  },
  setDistributionsSuccess({ commit }, { distribution, total_rows }) {
    commit(types.SET_DISTRIBUTIONS, distribution || []);
    commit(types.SET_QUERY_PARAMS, { queryParams: { rowsNumber: total_rows } });
  },
  clearDistributionCustomers({ commit }) {
    commit(types.CLEAR_DISTRIBUTION_CUSTOMERS);
    commit(types.CLEAR_TOTAL_DISTRIBUTION_CUSTOMERS);
  },
  async initDistribution({ commit, rootState, dispatch }) {
    const distribution = {
      imported: false,
      import_confirmed: false,
      delimiter: ',',
      name: '',
      import_invalid_lines: false,
      distribution_channel: null,
      schedule_at: null,
      validity_at: null,
      reminder_at: null,
      is_anonymous: false,
      template_survey_thanks_hash: null,
      template_survey_expired_hash: null,
      template_email_invite_hash: null,
      template_email_reminder_hash: null,
      template_smslink_invite_hash: null,
      template_smslink_reminder_hash: null,
      whatsapp_integration_uuid: null,
      whatsapp_template: {
        name: "",
        variables: [],
        language: "",
        body: ""
      },
      whatsapp_reminder_template: {
        name: "",
        variables: [],
        language: "",
        body: ""
      },
      language: "pt_BR"
    };

    const { survey } = rootState.survey;
    distribution.language = survey.default_lang;
    distribution.name = `${survey.name} ${new Date().toLocaleString()}`;

    distribution.scale_layout = await dispatch("getLayoutScaleEmail", distribution.language);
    commit(types.SET_DISTRIBUTION, { distribution });
    commit(types.SET_WHATSAPP_TEMPLATE_SCREEN_VARIABLES, { variables: [] });
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_SCREEN_VARIABLES, { variables: [] });
    dispatch("setMessageTemplateDefault");
  },
  setMessageTemplateDefault({ commit, rootState, state }) {
    /**
     * Load initial state for templates
     * Get the default template
     */
    const { distribution } = state;
    const { templates } = rootState.template;
    let templateThanksHash = null;
    let templateInviteHash = null;
    let templateRemenberHash = null;
    let templateExpiredHash = null;
    let templateSmsHash = null;
    let templateReminderSmsHash = null;
    templates.forEach((template) => {
      if (template.type === TEMPLATE_TYPES.SURVEY_COMPLETED && template.is_default) {
        templateThanksHash = template.public_hash;
      }
      if (template.type === TEMPLATE_TYPES.SURVEY_INVITE && template.is_default) {
        templateInviteHash = template.public_hash;
      }
      if (template.type === TEMPLATE_TYPES.SURVEY_REMINDER && template.is_default) {
        templateRemenberHash = template.public_hash;
      }
      if (template.type === TEMPLATE_TYPES.SURVEY_EXPIRED && template.is_default) {
        templateExpiredHash = template.public_hash;
      }
      if (template.type === TEMPLATE_TYPES.SURVEY_INVITE_LINKSMS && template.is_default) {
        templateSmsHash = template.public_hash;
      }
      if (template.type === TEMPLATE_TYPES.SURVEY_REMINDER_LINKSMS && template.is_default) {
        templateReminderSmsHash = template.public_hash;
      }
    });
    distribution.template_survey_thanks_hash = templateThanksHash;
    distribution.template_survey_expired_hash = templateExpiredHash;
    distribution.template_email_invite_hash = templateInviteHash;
    distribution.template_email_reminder_hash = templateRemenberHash;
    distribution.template_smslink_invite_hash = templateSmsHash;
    distribution.template_smslink_reminder_hash = templateReminderSmsHash;
    commit(types.SET_DISTRIBUTION, { distribution });
  },
  /**
   * Get the fist question in survey and mount the email layout for the scale
   */
  getLayoutScaleEmail({ rootState }, language) {
    // Get the first question
    let surveyItem = rootState.survey.survey.items[0];
    for (let i = 0; i < rootState.survey.survey.items.length; i++) {
      surveyItem = rootState.survey.survey.items[i];
      if (surveyItem.type !== QUESTION_TYPE.LABEL && surveyItem.type !== QUESTION_TYPE.IMAGE) {
        break;
      }
    }

    return getEmailLayout(surveyItem, language);
  },
  async getAll({ rootState, dispatch }) {
    try {
      const response = await DistributionService.getAll(rootState.organization.organization.uuid);
      if (response.data.data.distribution) {
        dispatch("setDistributionsSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllBySurvey({ rootState, dispatch }, { surveyUUID, pagination }) {
    try {
      const response = await DistributionService.getDistributionsBySurvey(
        rootState.organization.organization.uuid,
        surveyUUID,
        pagination
      );
      if (response.data.data.distribution) {
        dispatch("setDistributionsSuccess", response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getSegment({ rootState }, { surveyUUID, distributionUUID }) {
    return DistributionService.getSegment(rootState.organization.organization.uuid, surveyUUID, distributionUUID);
  },
  async getLinkList({ rootState }, { surveyUUID, distributionUUID, organizationUUID }) {
    const orgUUID = organizationUUID || rootState.organization.organization.uuid;
    return DistributionService.getLinkList(orgUUID, surveyUUID, distributionUUID);
  },
  async resend({ rootState, dispatch }, { surveyUUID, distributionUUID }) {
    try {
      await DistributionService.resend(rootState.organization.organization.uuid, surveyUUID, distributionUUID);
      dispatch("ui/showToastSuccess", i18n.t("distribution.messages.resend_distribution_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async delete({ rootState, dispatch }, { surveyUUID, distributionUUID }) {
    try {
      await DistributionService.delete(rootState.organization.organization.uuid, surveyUUID, distributionUUID);
      dispatch("ui/showToastSuccess", i18n.t("distribution.messages.delete_distribution_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      throw error;
    }
  },
  async create({ rootState, dispatch, state }, surveyUUID) {
    try {
      const valid = await dispatch("validate");
      if (!valid) {
        return false;
      }
      await DistributionService.create(rootState.organization.organization.uuid, surveyUUID, state.distribution);
      dispatch("ui/showToastSuccess", i18n.t("distribution.messages.create_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      throw error;
    }
  },
  async getAllResend({ rootState, dispatch }, { surveyUUID, distributionUUID }) {
    try {
      return await DistributionService.getAllResend(
        rootState.organization.organization.uuid,
        surveyUUID,
        distributionUUID
      );
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getCustomersByDistribution({ rootState, dispatch }, { surveyUUID, distributionUUID, pagination }) {
    try {
      const response = await DistributionService.getCustomersByDistribution(
        rootState.organization.organization.uuid,
        surveyUUID,
        distributionUUID,
        pagination
      );
      if (response.data.data.customer) {
        dispatch("setDistributionCustomers", response.data.data.customer);
        dispatch("setTotalDistributionCustomers", response.data.data.total_rows);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  setDistributionCustomers({ commit }, distributionCustomers) {
    commit(types.SET_DISTRIBUTION_CUSTOMERS, { distributionCustomers });
  },
  setDistributionImportConfirmed({ commit }, distributionImportConfirmed) {
    commit(types.SET_DISTRIBUTION_IMPORT_CONFIRMED, distributionImportConfirmed);
  },
  setDistributionImported({ commit }, distributionImported) {
    commit(types.SET_DISTRIBUTION_IMPORTED, distributionImported);
  },
  setTotalDistributionCustomers({ commit }, total) {
    commit(types.SET_TOTAL_DISTRIBUTION_CUSTOMERS, { total });
  },
  setDistribution({ commit }, distribution) {
    commit(types.SET_DISTRIBUTION, { distribution });
  },
  clearDistribution({ commit }) {
    commit(types.CLEAR_DISTRIBUTION);
  },
  togglePreview({ commit }) {
    commit(types.TOGGLE_PREVIEW);
  },
  checkSurveyDefaultLanguageInTemplate({ rootState, commit, state, getters }) {
    const { distribution } = state;
    const messageTemplates = rootState.template.templates;
    if (!distribution || !distribution.distribution_channel) {
      return;
    }
    if (
      distribution.distribution_channel !== DISTRIBUTION_CHANNEL.EMAIL &&
      distribution.distribution_channel !== DISTRIBUTION_CHANNEL.LINKSMS
    ) {
      return;
    }
    const templateType =
      distribution.distribution_channel === DISTRIBUTION_CHANNEL.EMAIL
        ? TEMPLATE_TYPES.SURVEY_INVITE
        : TEMPLATE_TYPES.SURVEY_INVITE_LINKSMS;

    const templateKey = getters.getTemplateKeyByType(templateType);
    const messageTemplateHash = distribution[templateKey];
    const template = messageTemplates.find((template) => template.public_hash === messageTemplateHash);
    if (template) {
      if (!template.languages.includes(distribution.language)) {
        commit(types.SET_LANGUAGE, template.items[0].language);
      }
    }
  },
  setMessageTemplate({ commit, getters }, { templateType, templateHash }) {
    commit(types.SET_MESSAGE_TEMPLATE, {
      templateType,
      templateHash,
      getters
    });
  },
  setLanguage({ commit }, language) {
    commit(types.SET_LANGUAGE, language);
  },
  setWhatsappIntegration({ commit }, integrationUUID) {
    commit(types.SET_WHATASTPP_INTEGRATION, { integrationUUID });
  },
  setChannel({ commit, dispatch, rootState, state }, channel) {
    const { survey } = rootState.survey;
    const stateName = state?.distribution?.name;
    const anonymosName = stateName?.replace(
      survey.name,
      `${`${survey.name}`}_${i18n.t("distribution.new.anonymous_name_survey")}`
    );
    const distName =
      channel === DISTRIBUTION_CHANNEL.LINK || channel === DISTRIBUTION_CHANNEL.QRCODE
        ? anonymosName
            .split(`_${i18n.t("distribution.new.anonymous_name_survey")}`)
            .filter(Boolean)
            .join(`_${i18n.t("distribution.new.anonymous_name_survey")}`)
        : stateName?.replace(`${`${survey.name}`}_${i18n.t("distribution.new.anonymous_name_survey")}`, survey.name);
    dispatch("setName", distName);
    dispatch("setIsCheckAnonymous", { selectedchannel: channel });
    commit(types.SET_CHANNEL, { channel });
  },
  setName({ commit }, name) {
    commit(types.SET_NAME, { name });
  },
  setIsCheckAnonymous({ dispatch, state, rootState }, { selectedchannel, checkedAnonymous }) {
    const { survey } = rootState.survey;
    const checkAnonymous =
      selectedchannel != DISTRIBUTION_CHANNEL.LINK &&
      selectedchannel != DISTRIBUTION_CHANNEL.QRCODE &&
      checkedAnonymous === true;
    const stateName = state?.distribution?.name;
    if (checkAnonymous) {
      const anonymosName = stateName?.replace(
        survey.name,
        `${`${survey.name}`}_${i18n.t("distribution.new.anonymous_name_survey")}`
      );
      dispatch("setName", anonymosName);
    } else {
      dispatch(
        "setName",
        stateName?.replace(`${`${survey.name}`}_${i18n.t("distribution.new.anonymous_name_survey")}`, survey.name)
      );
    }
  },
  setIsAnonymous({ commit, dispatch }, isAnonymous) {
    dispatch("setIsCheckAnonymous", { checkedAnonymous: isAnonymous });
    commit(types.SET_IS_ANONYMOUS, { isAnonymous });
  },
  setWhatsappTemplate({ commit }, whatsappTemplate) {
    commit(types.SET_WHATSAPP_TEMPLATES, { whatsappTemplate });
  },
  setImportInvalidLines({ commit }, importInvalidLines) {
    commit(types.SET_IMPORT_INVALID_LINES, { importInvalidLines });
  },
  setMapping({ commit }, mapping) {
    commit(types.SET_MAPPING, { mapping });
  },
  setDelimiter({ commit }, delimiter) {
    commit(types.SET_DELIMITER, { delimiter });
  },
  setCustomerSize({ commit }, customerSize) {
    commit(types.SET_CUSTOMER_SIZE, { customerSize });
  },
  setFile({ commit }, file) {
    commit(types.SET_FILE, { file });
  },
  setScaleLayout({ commit }, scaleLayout) {
    commit(types.SET_SCALE_LAYOUT, { scaleLayout });
  },
  setWhatsappTemplateBody({ commit }, body) {
    commit(types.SET_WHATSAPP_TEMPLATE_BODY, { body });
  },
  setWhatsappTemplateName({ commit }, name) {
    commit(types.SET_WHATSAPP_TEMPLATE_NAME, { name });
  },
  setWhatsappTemplateVariables({ commit }, variables) {
    commit(types.SET_WHATSAPP_TEMPLATE_VARIABLES, { variables });
  },
  setWhatsappTemplateScreenVariables({ commit }, variables) {
    commit(types.SET_WHATSAPP_TEMPLATE_SCREEN_VARIABLES, { variables });
  },
  setWhatsappTemplateLanguage({ commit }, language) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_LANGUAGE, { language });
  },
  setWhatsappReminderTemplateBody({ commit }, body) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_BODY, { body });
  },
  setWhatsappReminderTemplateName({ commit }, name) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_NAME, { name });
  },
  setWhatsappReminderTemplateVariables({ commit }, variables) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_VARIABLES, { variables });
  },
  setWhatsappReminderTemplateScreenVariables({ commit }, variables) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_SCREEN_VARIABLES, { variables });
  },
  setWhatsappReminderTemplateLanguage({ commit }, language) {
    commit(types.SET_WHATSAPP_REMINDER_TEMPLATE_LANGUAGE, { language });
  },
  async setValidityAt({ commit, state, dispatch }, validityAt) {
    dispatch("validator/removeError", "validity_at", { root: true });
    const validity = {
      validityAt,
      createdAt: state.distribution.created_at
    };
    if (!validateValidityCreatedAt(validity)) {
      await dispatch(
        "validator/addError",
        {
          field: "validity_at",
          message: i18n.t("distribution.messages.validity_inferior")
        },
        { root: true }
      );
    }

    commit(types.SET_VALIDITY_AT, { validityAt });
  },
  async setScheduleAt({ commit, state, dispatch }, scheduleAt) {
    dispatch("validator/removeError", "schedule_at", { root: true });
    const validity = {
      validityAt: scheduleAt,
      createdAt: state.distribution.created_at
    };
    if (!validateValidityCreatedAt(validity)) {
      await dispatch(
        "validator/addError",
        {
          field: "schedule_at",
          message: i18n.t("distribution.messages.validity_inferior")
        },
        { root: true }
      );
    }
    commit(types.SET_SCHEDULE_AT, { scheduleAt });
  },
  async setReminderAt({ commit, state, dispatch }, reminderAt) {
    dispatch("validator/removeError", "reminder_at", { root: true });
    const validity = {
      validityAt: reminderAt,
      createdAt: state.distribution.created_at
    };
    if (!validateValidityCreatedAt(validity)) {
      await dispatch(
        "validator/addError",
        {
          field: "reminder_at",
          message: i18n.t("distribution.messages.validity_inferior")
        },
        { root: true }
      );
    }
    commit(types.SET_REMINDER_AT, { reminderAt });
  },
  async setReminderAtField({ commit }, { field, value }) {
    commit(types.SET_REMINDER_AT_FIELD, { field, value });
  },
  resetReminderAtFields({ commit }) {
    commit(types.RESET_REMINDER_AT_FIELDS);
  },
  async validate({ dispatch, state, getters, rootState }) {
    dispatch("validator/clearValidator", null, { root: true });
    const messagesValid = validateMessages(state.distribution);
    if (messagesValid != null) {
      await dispatch("validator/addError", messagesValid, { root: true });
    }
    const scheduleValid = validateTabSchedule(state.distribution);
    if (scheduleValid != null) {
      await dispatch("validator/addError", scheduleValid, { root: true });
    }
    const customerValid = validateTabCustomer(state.distribution, getters.isDistributed);
    if (customerValid != null) {
      await dispatch("validator/addError", customerValid, { root: true });
    }
    const channelValid = validateTabChannel(state.distribution);
    if (channelValid != null) {
      await dispatch("validator/addError", channelValid, { root: true });
    }
    return rootState.validator.errors.length === 0;
  },
  mapCustomerImportData({ commit, state, rootState, dispatch }, data) {
    commit(types.SET_MAPPING, { mapping: MapCustomerData.mapCustomerImportFile(data) });
    const message = validateCustomerFile(state.distribution, rootState.organization.organization.identificator);
    if (message) {
      dispatch("ui/showToastError", message, { root: true });
      return false;
    }
    return true;
  },
  async getCustomersSupportHeaders({ rootState }) {
    const metadatas = rootState.metadata.metadatas || [];
    const customerMetadatas = metadatas.filter((metadata) => metadata.kind === METADATA_KIND.CUSTOMER);
    const suportedHeaders = ["name", "email", "phone"];
    if (metadatas) {
      customerMetadatas.forEach((metadata) => {
        suportedHeaders.push(metadata.name);
      });
    }
    return suportedHeaders;
  },
  createCustomerModelFile(context, supportedHeaders) {
    const row1 = [];
    const row2 = [];
    const row3 = [];
    supportedHeaders.forEach((header) => {
      row1.push(`${header} ${1}`);
      row2.push(`${header} ${2}`);
      row3.push(`${header} ${3}`);
    });
    const rows = [supportedHeaders, row1, row2, row3];

    const csvContent = `data:text/csv;charset=utf-8,${rows.map((e) => e.join(",")).join("\n")}`;
    const encodedUri = encodeURI(csvContent);
    return encodedUri;
  },
  detectDelimiterCSV(context, { lines, delimiters }) {
    return MapCustomerData.detectDelimiterCSV(lines, delimiters);
  },
  convertCSVLineToArray(context, { data, delimiter }) {
    return MapCustomerData.convertCSVLineToArray(data, delimiter);
  },
  removeBlankLines(context, lines) {
    return MapCustomerData.removeBlankLines(lines);
  },
  checkIfHaveLanguageInSurvey({ rootState }, language) {
    const { survey } = rootState.survey;
    if (language === survey.default_lang) {
      return true;
    }

    for (let i = 0; i < survey.lang.length; i++) {
      if (language === survey.lang[i]) {
        return true;
      }
    }

    return false;
  }
};
